/**
 * @fileoverview System-web page that hosts the tenant-scoped import run wizard and binds a single wizard launch to a unique resume scope.
 * @module system-web/import-wizard-page
 *
 * Responsibilities:
 * - Resolve the selected department and optional template id from route state.
 * - Hand prefilled files from Gate 3 save-and-import into the run wizard.
 * - Mint a launch-scoped nonce so draft import-run resume applies only to the current import launch.
 *
 * Dependencies:
 * - React Router page state and params
 * - `UnifiedImportWizard` from `@firesquirrel/import-editor` (the legacy
 *   SystemImportWizardContainer is retired; Phase 2A, G-002)
 * - `buildSystemImportWizardServices` — the system-web services adapter
 * - `tenantService`
 *
 * Architecture:
 * - Page composition only; no direct API calls.
 * - Tenant selection stays in system-web UI state while the wizard delegates reads/writes to services.
 * - Launch-scoped resume keys prevent stale draft import-run ids from leaking into unrelated imports.
 *
 * Notes:
 * - Gate 3 may prefill a `File` through router history state.
 * - Fresh visits from the template library must not silently resume another import attempt.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { ChevronLeft, ArrowRight } from 'lucide-react';
import type { ImportTemplateSampleBlob, ImportTemplateSamplePreview } from '@firesquirrel/local-db';
import { UnifiedImportWizard } from '@firesquirrel/import-editor';
import { buildSystemImportWizardServices } from '../components/unifiedWizardAdapter';
import { listTenants } from '../services/tenantService';

function createLaunchNonce(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `launch-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getLaunchNonceStorageKey(locationKey: string): string {
  return `system-import-launch:${locationKey}`;
}

/**
 * @component ImportWizardPage
 * Purpose:     Hosts the tenant-scoped import run wizard for system-web, binding a
 *              single wizard launch to a unique resume scope via a launch-scoped nonce.
 * Props:       (none — route component; reads templateId from URL params)
 * Behavior:    Resolves the selected department and optional template id from route
 *              state; hands prefilled files from Gate 3 save-and-import into the run
 *              wizard; mints a launch-scoped nonce so draft import-run resume applies
 *              only to the current import launch.
 * Data Source: local (Dexie via the services adapter); tenant list from tenantService
 * Constraints: no direct API calls; all data flows through the services adapter
 * Performance: services built once per mount via useMemo
 */
export default function ImportWizardPage() {
  const { templateId } = useParams<{ templateId?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  // File forwarded from the Card 3 save-and-import flow on the
  // UnifiedImportWizardPreviewPage. React Router clones history state
  // via structuredClone, which preserves File instances intact — so
  // the user does NOT have to re-upload what they just mapped.
  const handoffState = location.state as {
    prefilledFile?: File;
    prefilledSampleBlob?: ImportTemplateSampleBlob;
    prefilledSamplePreview?: ImportTemplateSamplePreview;
  } | null;
  const prefilledFile: File | undefined = handoffState?.prefilledFile;
  const prefilledSampleBlob: ImportTemplateSampleBlob | undefined = handoffState?.prefilledSampleBlob;
  const prefilledSamplePreview: ImportTemplateSamplePreview | undefined = handoffState?.prefilledSamplePreview;
  const [tenants, setTenants] = useState<any[]>([]);
  const initialTenantIdFromQuery = searchParams.get('tenantId') ?? '';
  const [draftTenantId, setDraftTenantId] = useState<string>(initialTenantIdFromQuery);
  const [activeTenantId, setActiveTenantId] = useState<string>(initialTenantIdFromQuery);
  const [launchNonce] = useState<string>(() => {
    const storageKey = getLaunchNonceStorageKey(location.key || 'default');
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return createLaunchNonce();
    }
    const existing = window.sessionStorage.getItem(storageKey);
    if (existing) return existing;
    const created = createLaunchNonce();
    window.sessionStorage.setItem(storageKey, created);
    return created;
  });

  useEffect(() => {
    listTenants()
      .then((rows) => {
        setTenants(rows);
        const fromQuery = searchParams.get('tenantId');
        if (fromQuery && rows.some(r => r._id === fromQuery)) {
          setDraftTenantId(fromQuery);
          setActiveTenantId(fromQuery);
        } else if (rows.length === 1) {
          setDraftTenantId(rows[0]._id);
        }
      })
      .catch(() => setTenants([]));
  }, [searchParams]);

  const selectedTenant = tenants.find((tenant) => tenant._id === activeTenantId) ?? null;

  // Adapter is bound to the active tenant for run operations; rebuild only
  // when the tenant changes so the wizard's service closures stay stable.
  const wizardServices = useMemo(
    () => buildSystemImportWizardServices({ tenantId: activeTenantId || undefined }),
    [activeTenantId],
  );

  const handleTenantChange = (tenantId: string) => {
    setDraftTenantId(tenantId);
  };

  const handleContinue = () => {
    if (!draftTenantId) return;
    setActiveTenantId(draftTenantId);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tenantId', draftTenantId);
      return next;
    }, { replace: true });
  };

  const handleClearDepartment = () => {
    setDraftTenantId('');
    setActiveTenantId('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('tenantId');
      return next;
    }, { replace: true });
  };

  const handleWizardComplete = () => {
    if (!activeTenantId) {
      navigate('/import-runs');
      return;
    }
    navigate(`/import-runs?tenantId=${encodeURIComponent(activeTenantId)}`);
  };

  const handleWizardCancel = () => {
    if (!activeTenantId) {
      navigate('/import-runs');
      return;
    }
    setDraftTenantId(activeTenantId);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (activeTenantId) next.set('tenantId', activeTenantId);
      else next.delete('tenantId');
      return next;
    }, { replace: true });
    navigate('/import-runs');
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="shrink-0 px-6 py-4 border-b bg-white flex items-center gap-4">
        <button type="button" onClick={() => navigate('/import-runs')}
          className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="flex-1 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">New Import Run</h1>
            <p className="text-sm text-gray-500 mt-0.5">Step 1: choose a department. Step 2: choose the template and file for the import.</p>
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0 p-6">
        {activeTenantId ? (
          <div className="h-full min-h-0 flex flex-col gap-4">
            <div className="shrink-0 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 flex items-center justify-between gap-4">
              <div>
                <div className="text-xs font-semibold text-gray-700">Department</div>
                <div className="text-sm text-gray-900 mt-0.5">{selectedTenant?.name || selectedTenant?.slug || activeTenantId}</div>
              </div>
              <button
                type="button"
                onClick={handleClearDepartment}
                className="text-xs text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md px-3 py-1.5 bg-white"
              >
                Change department
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <UnifiedImportWizard
                services={wizardServices}
                intent="new-import"
                templateId={templateId}
                initialFile={prefilledFile}
                onDone={handleWizardComplete}
                onCancel={handleWizardCancel}
                runFlow={{
                  initialSampleBlob: prefilledSampleBlob,
                  initialSamplePreview: prefilledSamplePreview,
                  draftRunPersistence: {
                    resumeStorageKey: `system-import-run:${launchNonce}`,
                  },
                  defaultTemplateVisibility: 'system-only',
                  fileInputId: 'sys-import-file-input',
                  hideTemplatePickerWhenSeeded: true,
                  onComplete: handleWizardComplete,
                  chrome: {
                    freeStepNavigation: false,
                    showTemplateBanner: true,
                    backLabel: 'Back',
                  },
                }}
              />
            </div>
          </div>
        ) : (
          <div className="max-w-2xl rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="text-sm font-semibold text-gray-900">Choose a department to start the import</div>
            <p className="text-sm text-gray-500 mt-1">This selection defines where the run will execute. After you choose the department, the wizard will open and you can pick the import template for that run.</p>
            <div className="mt-5 flex flex-col gap-2 max-w-md">
              <label className="text-xs font-semibold text-gray-700">Department</label>
              <select
                className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800"
                value={draftTenantId}
                onChange={e => handleTenantChange(e.target.value)}
              >
                <option value="">Select a department…</option>
                {tenants.map((tenant) => (
                  <option key={tenant._id} value={tenant._id}>{tenant.name || tenant.slug || tenant._id}</option>
                ))}
              </select>
            </div>
            <div className="mt-5 flex items-center gap-3">
              <button
                type="button"
                disabled={!draftTenantId}
                onClick={handleContinue}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => navigate('/import-runs')}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
